import React, { useState, useEffect } from 'react';
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';

const BounceListReport = () => {
    const [userList, setUsersList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [emails, setEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState('');
    const BUId = sessionStorage.getItem('BUId');

    useEffect(() => {
        const fetchEmails = async () => {
            try {
                const emailResponse = await fetch(`${process.env.REACT_APP_API_URL}/Mailer/GetFromMails?BUId=${BUId}`);
                if (!emailResponse.ok) {
                    throw new Error('Failed to fetch emails');
                }
                const emailData = await emailResponse.json();
                setEmails(emailData);
            } catch (error) {
                console.error('Error fetching emails:', error);
            }
        };

        fetchEmails();
    }, [BUId]);

    // Function to handle form submission and call the SMTPBounce API
    const handleSubmit = async () => {
        if (!selectedEmail) {
            alert('Please select an email');
            return;
        }

        try {
            const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/Mailer/SMTPBounce?MailId=${selectedEmail}`);
            if (!userResponse.ok) {
                throw new Error('Failed to fetch data');
            }
            const data = await userResponse.json();
            console.log("Data from API:", data);
            setUsersList(data);
            setCurrentPage(1);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    // Fixed filtering logic to correctly filter by 'to' field and reason
    const filteredUserList = userList.filter(user => {
        return user.reason && user.reason.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Format date function
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return format(date, 'MMM dd, yyyy HH:mm');
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString; // Return original string if formatting fails
        }
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredUserList.slice(indexOfFirstItem, indexOfLastItem);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const totalPages = Math.ceil(filteredUserList.length / itemsPerPage);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="container mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
                {/* Header */}
                <div className="bg-gray-200 p-4">
                    <h1 className="text-xl">Bounce Mails List Report</h1>
                </div>

                {/* Dropdown for Email Selection */}
                <div className="p-4 bg-gray-100 border-b">
                    <label htmlFor="emailDropdown" className="text-sm font-semibold text-gray-700">Choose Email</label>
                    <div className="flex items-center space-x-4">
                        <select
                            id="emailDropdown"
                            value={selectedEmail}
                            onChange={(e) => setSelectedEmail(e.target.value)}
                            className="w-full p-3 text-gray-700 bg-white rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select an Email</option>
                            {emails.length > 0 ? (
                                emails.map((emailObj, index) => (
                                    emailObj.fromEmails && emailObj.fromEmails.map((email, emailIndex) => (
                                        <option
                                            key={`${index}-${emailIndex}`}
                                            value={email}
                                            className="py-2 block overflow-hidden text-ellipsis"
                                        >
                                            {email}
                                        </option>
                                    ))
                                ))
                            ) : (
                                <option disabled>No Emails Available</option>
                            )}
                        </select>

                        <button
                            onClick={handleSubmit}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg shadow-md hover:bg-blue-600 focus:outline-none"
                        >
                            Submit
                        </button>
                    </div>
                </div>

                {/* Search field */}
                <div className="p-4 bg-white">
                    <input
                        type="text"
                        placeholder="Search by reason..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Table */}
                <div className="w-full overflow-x-auto bg-white rounded-lg shadow">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-500 to-purple-600">
                                <th className="px-6 py-3 text-left text-sm font-semibold text-white">Email Address</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-white">Subject</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-white">Reason</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-white">Date</th>                              
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {currentItems && currentItems.length > 0 ? (
                                currentItems.map((user, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">{user.to || 'N/A'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-900">{user.subject || 'N/A'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-900">{user.reason || 'N/A'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-900">{formatDate(user.receivedDate)}</td>                                   
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-sm text-center text-gray-600">
                                        {userList.length > 0 ? 'No matching records found' : 'No bounce details found. Please select an email and submit.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-gray-100 px-4 py-3 flex items-center justify-center space-x-2">
                        <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>

                        {[...Array(totalPages)].map((_, index) => (
                            <button
                                key={index}
                                onClick={() => paginate(index + 1)}
                                className={`px-4 py-2 text-sm font-medium rounded-lg border 
                                ${currentPage === index + 1
                                    ? 'bg-blue-500 text-white border-blue-500'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                {index + 1}
                            </button>
                        ))}

                        <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BounceListReport;
